import { useParams } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { match } from 'ts-pattern'

import {
  formatPaymentDetails,
  getInvoiceTitle,
  resolveServicePeriods,
  InvoiceRendererData
} from '@sequencehq/invoice-content'
import { formatAddress } from '@sequencehq/utils'
import { format } from '@sequencehq/utils/dates'
import { Currency, useQuery } from '@sequencehq/api'
import type { CountriesAlpha2 } from '@sequencehq/api/commonEnums'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  dashboardv20240509Client,
  Dashboardv20240509Api
} from '@sequencehq/api/dashboard/v20240509'
import { clientInvoiceAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { DATE_FORMAT_API } from 'Invoices/constants'
import { getCurrentMonthDateRange, resolveInvoiceDueDate } from 'Invoices/utils'
import { currentCreditNotes } from 'Invoices/utils/currentlyApplied'
import { AggregatedInvoice } from 'Invoices/types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const useInvoiceRendererData = (): InvoiceRendererData | undefined => {
  const { id: idParam } = useParams()

  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const { showServicePeriodsAlways, usePdfInvoiceAlternativeHeader } =
    useFeatureFlags()

  const { data: merchantData } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const { data: accountSettingsData } = useQuery(
    dashboard20240730Client.getSequenceAccountSettings
  )

  const { data: dueDateSettingsData } = useQuery(
    dashboardv20240509Client.getInvoiceAccountingDateSettings
  )

  const merchantDetailsCountry =
    clientInvoice?.customerBillingAddress?.country ??
    merchantData?.address.country

  const { data: invoiceMerchantData } = useQuery(
    dashboard20240730Client.getInvoiceMerchantDetails,
    {
      currency: clientInvoice?.currency ?? 'USD',
      country: merchantDetailsCountry as CountriesAlpha2,
      state: clientInvoice?.customerBillingAddress?.state
    },
    { enabled: Boolean(merchantDetailsCountry) }
  )

  const { data: currencyConversions } = useQuery(
    dashboard20240730Client.getInvoiceCurrencyConversions,
    {
      id: idParam ?? ''
    },
    {
      enabled: !!idParam
    }
  )

  const isClientInvoiceLoaded = !idParam || (!!idParam && !!clientInvoice)

  if (
    !isClientInvoiceLoaded ||
    !merchantData ||
    !accountSettingsData ||
    !invoiceMerchantData ||
    !dueDateSettingsData
  ) {
    return undefined
  }

  return toInvoiceRendererData({
    clientInvoice,
    defaultCurrency: accountSettingsData.enabledCurrencies.at(0),
    merchantData,
    invoiceMerchantData,
    currencyConversions: currencyConversions?.items ?? [],
    invoiceDueDateCalculatedFrom:
      dueDateSettingsData.invoiceDueDateCalculatedFrom,
    alwaysShowServicePeriod: showServicePeriodsAlways,
    usePdfInvoiceAlternativeHeader,
    // The live preview links credit notes to their dashboard page.
    buildCreditNoteUrl: creditNoteId =>
      `${window.location.origin}/credit-notes/${creditNoteId}`
  })
}

type InvoiceMerchantDetails =
  DashboardApi20240730.GetInvoiceMerchantDetails.InvoiceMerchantDetails

export const toInvoiceRendererData = ({
  clientInvoice,
  defaultCurrency,
  merchantData,
  invoiceMerchantData,
  currencyConversions,
  invoiceDueDateCalculatedFrom,
  alwaysShowServicePeriod = false,
  usePdfInvoiceAlternativeHeader = false,
  buildCreditNoteUrl
}: {
  clientInvoice: AggregatedInvoice | null
  defaultCurrency: Currency | undefined
  merchantData: {
    defaultDueDateDays: number
    customerPortalEnabled: boolean
  }
  invoiceMerchantData: InvoiceMerchantDetails
  currencyConversions: DashboardApi20240730.GetInvoiceCurrencyConversions.CurrencyConversion[]
  invoiceDueDateCalculatedFrom: Dashboardv20240509Api.GetInvoiceAccountingDateSettings.InvoiceDueDateCalculatedFrom
  alwaysShowServicePeriod?: boolean
  usePdfInvoiceAlternativeHeader?: boolean
  // Builds the link target for a credit note's settlement row. The live
  // preview links to the dashboard credit note page; client-side PDF renders
  // link to the customer portal, mirroring urlViewOnline. Rows render as
  // plain text when omitted (or when the builder returns undefined).
  buildCreditNoteUrl?: (creditNoteId: string) => string | undefined
}): InvoiceRendererData => {
  const NOW = new Date()

  const billingPeriod = clientInvoice?.billingPeriod ?? {
    start: format(getCurrentMonthDateRange(NOW).startDate, DATE_FORMAT_API),
    endInclusive: format(getCurrentMonthDateRange(NOW).endDate, DATE_FORMAT_API)
  }

  return {
    invoiceNumber: clientInvoice?.invoiceNumber,
    currency: clientInvoice?.currency ?? defaultCurrency ?? 'USD',
    accountingDate:
      clientInvoice?.accountingDate ?? format(NOW, DATE_FORMAT_API),
    issueDate: clientInvoice?.issueDate,
    dueDate: calculateDueDate({
      clientInvoice,
      calculatedFrom: invoiceDueDateCalculatedFrom,
      now: NOW,
      defaultDueDateDays: merchantData.defaultDueDateDays
    }),
    dueDatePolicy: clientInvoice
      ? clientInvoice.dueDatePolicy
      : {
          type: 'PAYMENT_TERMS',
          paymentTermDays: merchantData.defaultDueDateDays
        },
    status: clientInvoice?.status ?? 'DRAFT',
    netTotal: clientInvoice?.netTotal ?? '0',
    grossTotal: clientInvoice?.grossTotal ?? '0',
    billingPeriod,
    memo: clientInvoice?.memo,
    purchaseOrderNumber: clientInvoice?.purchaseOrderNumber,
    reference: clientInvoice?.reference,
    lineItemGroups: resolveServicePeriods({
      lineItemGroups: normaliseLineItemGroups(
        clientInvoice?.lineItemGroups ?? []
      ),
      billingPeriod,
      alwaysShow: alwaysShowServicePeriod
    }),
    urlPayOnline:
      !!clientInvoice?.paymentOptions?.includes('LINK') &&
      clientInvoice.renderSettings?.paymentLinkDisplay === 'SHOW_PAYMENT_LINK'
        ? '#'
        : undefined,
    showAutoChargeMessage: !!clientInvoice?.paymentOptions?.includes('LINK'),
    usePdfInvoiceAlternativeHeader,
    urlViewOnline: merchantData.customerPortalEnabled ? '#' : undefined,
    invoiceTitle:
      clientInvoice?.customerBillingAddress && clientInvoice?.customerTaxStatus
        ? getInvoiceTitle({
            customerCountry: clientInvoice.customerBillingAddress.country,
            customerTaxStatus: clientInvoice.customerTaxStatus
          })
        : undefined,
    /**
     * Merchant
     */
    merchantLegalCompanyName:
      clientInvoice?.merchantDetails?.legalCompanyName ??
      invoiceMerchantData.legalCompanyName,
    merchantEmail:
      clientInvoice?.merchantDetails?.email ?? invoiceMerchantData.email,
    merchantAddress: formatAddress(
      clientInvoice?.merchantDetails?.address ?? invoiceMerchantData.address
    ),
    merchantLogoUrl:
      clientInvoice?.merchantDetails?.logoUrl ?? invoiceMerchantData.logoUrl,
    merchantTaxId:
      clientInvoice?.merchantDetails?.taxId ?? invoiceMerchantData.taxId,
    /**
     * Customer
     */
    customerTaxId: clientInvoice?.customerTaxId,
    customerLegalCompanyName: clientInvoice?.customerLegalCompanyName ?? '',
    customerAddress: formatAddress(clientInvoice?.customerBillingAddress),
    customerTaxStatus: clientInvoice?.customerTaxStatus,
    paymentDetails: formatPaymentDetails(invoiceMerchantData),
    subAccountUsage: clientInvoice?.subAccountUsage ?? [],
    /**
     * Not displayed in React invoice preview
     */
    currencyConversions,
    creditBalances: [],
    seatBreakdown: [],
    /**
     * Settlement (applied credit notes and credit grants). amountDue is the
     * outstanding balance after both are applied, falling back to grossTotal so
     * non-credited invoices (or payloads predating these fields) render
     * unchanged. Each note's settlement row links to its credit note page when
     * the id is known; grants have no detail page, so their rows are plain text.
     * URLs must be absolute: they're also embedded as PDF link annotations in
     * client-side renders, where relative URIs are dropped by pdf.js and
     * wouldn't survive a download anyway.
     */
    amountDue: clientInvoice?.balance ?? clientInvoice?.grossTotal ?? '0',
    appliedCreditNotes: (clientInvoice
      ? currentCreditNotes(clientInvoice)
      : []
    ).map(creditNote => ({
      ...creditNote,
      url: creditNote.id ? buildCreditNoteUrl?.(creditNote.id) : undefined
    })),
    appliedCreditGrants: clientInvoice?.appliedCreditGrants ?? [],
    appliedCreditGrantTax: clientInvoice?.appliedCreditGrantTax
  }
}

const calculateDueDate = ({
  clientInvoice,
  calculatedFrom,
  now,
  defaultDueDateDays
}: {
  clientInvoice: AggregatedInvoice | null
  calculatedFrom: Dashboardv20240509Api.GetInvoiceAccountingDateSettings.InvoiceDueDateCalculatedFrom
  now: Date
  defaultDueDateDays: number
}): string | undefined => {
  /**
   * Never show the due date for IN_PROGRESS invoices
   */
  if (clientInvoice?.status === 'IN_PROGRESS') return undefined

  /**
   * On `/invoices/new` before the draft exists there is no policy to read, so
   * a bare invoice on today's date resolves to the merchant's own default.
   */
  const resolution = resolveInvoiceDueDate({
    calculatedFrom,
    dueDate: clientInvoice?.dueDate,
    dueDatePolicy: clientInvoice?.dueDatePolicy ?? null,
    paymentTermDays: clientInvoice?.paymentTermDays ?? null,
    accountingDate:
      clientInvoice?.accountingDate ?? format(now, DATE_FORMAT_API),
    defaultDueDateDays
  })

  return (
    match(resolution)
      .with({ type: 'RESOLVED' }, ({ dueDate }) => dueDate)
      // The preview renders its skeleton and drops the header/footer due date
      .with({ type: 'PENDING' }, () => undefined)
      .exhaustive()
  )
}

/**
 * Dashboard line items store service periods as a nested object
 * ({ startDate, endDate }), but resolveServicePeriods expects flat
 * fields (servicePeriodStart, servicePeriodEnd). This adapter normalises.
 */
export const normaliseLineItemGroups = (
  lineItemGroups: AggregatedInvoice['lineItemGroups']
) =>
  lineItemGroups.map(group => ({
    ...group,
    lineItems: group.lineItems.map(li => ({
      ...li,
      servicePeriodStart: li.servicePeriodStart ?? li.servicePeriod?.startDate,
      servicePeriodEnd: li.servicePeriodEnd ?? li.servicePeriod?.endDate
    }))
  }))
